import React, { type ReactNode } from 'react';
import type { FormControlOptionModelExtended } from '../types';
import type { useFormFieldResult } from '~/shared/forms/useFormField';
import { CheckboxGroupFormField, type FormCheckboxGroupFieldProps } from '~/shared/forms/controls/fields/CheckboxGroupFormField';
import { Checkbox } from '~/shared/forms/controls/base-controls/Checkbox';
import { cn } from '~/shared/components/utils';

export function CheckboxGroupFormFieldWithChildren(
	props: Omit<FormCheckboxGroupFieldProps, 'options'> & { options: FormControlOptionModelExtended[] }
) {
	const mustBeOnlySelectedValue = props.options.find(
		option => typeof option !== 'string' && 'mustBeOnlySelectedValue' in option && option.mustBeOnlySelectedValue
	) as FormControlOptionModelExtended;

	const onSelectionChange = async (value: string[] | number[], fieldProps: useFormFieldResult<string[]>) => {
		const fieldValueStrings = value.map((x: string | number) => x.toString());

		if (mustBeOnlySelectedValue && fieldValueStrings.length > 1 && fieldValueStrings.includes(mustBeOnlySelectedValue.value)) {
			await fieldProps.onChange([mustBeOnlySelectedValue.value.toString()]);
			return;
		}

		await fieldProps.onChange(fieldValueStrings);
	};

	const getOptions = (fieldProps: useFormFieldResult<string[]>): ReactNode => {
		return props.options.map(option => {
			const optionIsChecked = fieldProps.value.includes(option.value);
			const className = cn(option.className && option.className, optionIsChecked && option.selectedClassName && option.selectedClassName);
			if (!option.children) {
				return <Checkbox {...option} key={option.value} isDisabled={!!option?.disabled} className={className} isSelected={optionIsChecked} />;
			}

			return (
				<div key={option.value} className={className}>
					<Checkbox {...option} isDisabled={!!option?.disabled} className={undefined} value={option.value} />
					{option.children && optionIsChecked ? <div className="space-y-4 p-2 pl-8">{option.children}</div> : null}
				</div>
			);
		});
	};
	return <CheckboxGroupFormField {...props} options={undefined} getOptions={getOptions} onChangeOverride={onSelectionChange} />;
}
